import React from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaTimes, FaSearch, FaSearchMinus } from 'react-icons/fa';
import { useVideoImageCarousel } from './useVideoImageCarousel';
import { VideoImageCarouselProps } from './types';
import { normalizeVideoImagePath } from '../../utils/videoImagePath';
import styles from './VideoImageCarousel.module.css';

/**
 * 画像カルーセルコンポーネント
 * 画像を表示し、拡大表示機能を提供します
 */
const VideoImageCarousel: React.FC<VideoImageCarouselProps> = ({ images }) => {
  const {
    // 状態変数
    currentImageIndex,
    modalImage,
    modalImageIndex,
    isZoomed,
    zoomLevel,
    isDragging,
    position,
    sortedImages,
    slideDirection,
    isModalTransitioning,
    swipeDistance,
    
    // Refs
    modalRef,
    imageRef,
    carouselRef,
    
    // 関数
    prevImage,
    nextImage,
    openModal,
    closeModal,
    prevModalImage,
    nextModalImage,
    toggleZoom,
    adjustZoom,
    resetZoom,
    
    // ハンドラー
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleModalContentClick,
    
    // 定数
    swipeThreshold
  } = useVideoImageCarousel(images);

  if (!sortedImages || sortedImages.length === 0) {
    return null;
  }

  const currentImageSrc = normalizeVideoImagePath(sortedImages[currentImageIndex].image_path);

  return (
    <>
      <div className={styles.videoCarouselWrapper}>
        <div className={styles.carouselContainer}>
          <button 
            onClick={prevImage} 
            className={`${styles.carouselButton} ${styles.prevButton}`} 
            aria-label="前の画像"
          >
            <FaChevronLeft />
          </button>
          
          <div className={styles.carouselImageContainer}>
            <img
              src={currentImageSrc}
              alt={sortedImages[currentImageIndex].description || 'カルーセル画像'}
              className={styles.carouselImage}
              onClick={() => openModal(currentImageSrc, currentImageIndex)}
              data-testid="video-image-carousel__image"
            />
            <div className={styles.expandIndicator}>
              <FaExpand />
            </div>
          </div>
          
          <button 
            onClick={nextImage} 
            className={`${styles.carouselButton} ${styles.nextButton}`} 
            aria-label="次の画像"
          >
            <FaChevronRight />
          </button>
          
          <div className={styles.imageCounterContainer}>
            <div className={styles.expandHint}>
              <FaSearch /> <span>クリックで拡大</span>
            </div>
            <div className={styles.imageCount} data-testid="video-image-carousel__counter">
              {currentImageIndex + 1} / {sortedImages.length}
            </div>
          </div>
        </div>
      </div>

      {/* モーダル - ポータルを使用して、DOMの最上位にレンダリング */}
      {modalImage && createPortal(
        <div 
          className={styles.modalOverlay} 
          onClick={closeModal}
          ref={modalRef}
          data-testid="video-image-carousel__modal"
        >
          <div
            className={`${styles.modalContent} ${isZoomed ? styles.zoomed : ''}`}
            onClick={handleModalContentClick}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalInfo}>
                <span className={styles.modalCounter}>
                  {modalImageIndex + 1} / {sortedImages.length}
                </span>
                <span className={styles.zoomLevel}>{Math.round(zoomLevel * 100)}%</span>
              </div>
              
              <div className={styles.modalControls}>
                <button 
                  className={styles.modalControlButton} 
                  onClick={() => adjustZoom(-0.5)} 
                  disabled={zoomLevel <= 1}
                  aria-label="縮小"
                  data-testid="video-image-carousel__zoom-out"
                >
                  <FaSearchMinus />
                </button>
                <button 
                  className={styles.modalControlButton} 
                  onClick={() => adjustZoom(0.5)}
                  disabled={zoomLevel >= 4} 
                  aria-label="拡大"
                  data-testid="video-image-carousel__zoom-in"
                >
                  <FaSearch />
                </button>
                <button 
                  className={styles.modalControlButton} 
                  onClick={resetZoom} 
                  disabled={zoomLevel === 1}
                  aria-label="ズームをリセット"
                  data-testid="video-image-carousel__zoom-reset"
                >
                  {isZoomed ? <FaCompress /> : <FaExpand />}
                </button>
                <button 
                  className={styles.modalCloseButton} 
                  onClick={closeModal} 
                  aria-label="閉じる"
                  data-testid="video-image-carousel__close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            
            <div 
              className={`${styles.modalImageContainer} ${slideDirection ? styles[slideDirection] : ''} ${isModalTransitioning ? styles.transitioning : ''}`}
              style={{ 
                cursor: isDragging ? 'grabbing' : isZoomed ? 'grab' : 'zoom-in',
                transform: !isZoomed && swipeDistance ? `translateX(${swipeDistance * 0.3}px)` : 'none'
              }}
            >
              <img
                ref={imageRef}
                src={modalImage}
                alt="拡大表示"
                className={`${styles.modalImage} ${isZoomed ? styles.zoomed : ''}`}
                style={{
                  transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                  transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)'
                }}
                onClick={toggleZoom}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                draggable="false"
                data-testid="video-image-carousel__modal-image"
              />
              
              {/* ズームガイド（ズーム時の操作ヘルプ） */}
              {isZoomed && (
                <div className={styles.zoomGuide}>
                  <div className={styles.zoomGuideIcon}></div>
                  <span className={styles.zoomGuideText}>ドラッグして画像を移動</span>
                </div>
              )}
            </div>
            
            {sortedImages.length > 1 && (
              <>
                <button 
                  className={`${styles.modalNavButton} ${styles.modalPrev}`} 
                  onClick={prevModalImage}
                  aria-label="前の画像"
                  data-testid="video-image-carousel__prev-modal"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className={`${styles.modalNavButton} ${styles.modalNext}`} 
                  onClick={nextModalImage}
                  aria-label="次の画像"
                  data-testid="video-image-carousel__next-modal"
                >
                  <FaChevronRight />
                </button>
              </>
            )}
            
            <div className={styles.modalHelpText}>
              {isZoomed ? 
                '画像をクリック: 元のサイズに戻す | ドラッグ: 画像を移動 | マウスホイール: 拡大/縮小' :
                '矢印キー: 画像切替 | 画像をクリック: 拡大 | マウスホイール: 拡大/縮小 | ESC: 閉じる'
              }
            </div>
            
            {/* モバイル用の操作説明 */}
            <div className={styles.mobileHelpText}>
              {isZoomed ? 
                'タップ: 元のサイズに戻す | ドラッグ: 画像を移動 | ピンチ: 拡大/縮小' :
                'スワイプ: 画像切替 | ダブルタップ: 拡大'
              }
            </div>
          </div>
        </div>,
        document.body // bodyの直下にモーダルを配置
      )}
    </>
  );
};

export default VideoImageCarousel; 
